import React from "react";
import get from "lodash/get";
import forEach from "lodash/forEach";
import map from "lodash/map";
import find from "lodash/find";
import moment from "moment-timezone";
import { Link } from "react-router-dom";
import { connect } from "react-redux";
import {
  Row,
  Col,
  Progress,
  Select,
  Icon,
  DatePicker,
  Table,
  Button,
  Descriptions,
} from "antd";
import { Bar, HorizontalBar } from "react-chartjs-2";
import { Helmet } from "react-helmet";

import classes from "./AdminDashboard.css";

import { logout } from "../../reducers/user/actions";
import isAuthenticated from "../../utils/isAuthenticated";
import {
  getDashboardRequest,
  getSitesRequest,
  getFollowUpsRequest,
  getRandomizationConfig,
  getDashboardConsentRequest,
} from "../../api/requests";
import { withParams } from "../../utils/url";
import {
  getDashboardPermission,
  getDashboardEnrolmentStagesPermission,
  getDashboardFollowUp7dPermission,
  getDashboardReadDeviationPermission,
  getDashboardReadSAEPermission,
  getDashboardReadEnrolmentBySitePermission,
  getDashboardDrugAvailabilityPermission,
  getDashboardInvoiceCountPermission,
  getDashboardInvoiceAmountPermission,
  getUserTimezone,
  getUserShortTimezone,
  getDrugManagementStatus,
  getProjectConfigurationStatus,
  getDashboardReadFollowupPermission,
  getViewConsentDashboardChartsPermission,
} from "../../reducers/user/selectors";
import { withNamespaces } from "react-i18next";

import colors, {blueGreen, blueLight, gray, green, greenDark, orange} from "./colors";

const routes = require("../../routes.json");

const { Option } = Select;
const { RangePicker } = DatePicker;

const capitalize = (s) => {
  return s[0].toUpperCase() + s.slice(1);
};

class AdminDashboard extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      dashboard: {},
      consentData: {},
      filter: "",
      site: "",
      sites: [],
      dateRange: [moment(), moment().add(7, "days")],
      followUpsConfig: undefined,
      followup_filter: null,
      followup_type: null,
      is_30_days: false,
      followups: [],
      currentPage: 1,
      total: 1,
      pageSize: 25,
      isLoadingFollowUps: false,
      currency: "CAD",
    };
  }

  async componentDidMount() {
    this.getDashboardData();
    this.getRandomizationConfig();

    try {
      const { data } = await getSitesRequest();
      this.setState({ sites: data });
    } catch (e) {
      if (get(e, "response.status") === 401) {
        this.props.logout();
        return;
      }
      console.log(e);
    }
  }

  columns() {
    return [
      {
        dataIndex: "site_id",
        key: "site_id",
        title: this.props.i18n.t("Site"),
        render: (val) => (
          <span>
            {get(
              find(this.state.sites, ({ id }) => id === val),
              "code"
            )}{" "}
            -{" "}
            {get(
              find(this.state.sites, ({ id }) => id === val),
              "name"
            )}
          </span>
        ),
      },
      {
        dataIndex: "subject",
        key: "subject",
        title: this.props.i18n.t("Subject"),
        render: (val) => <span>{get(val, "code")}</span>,
      },
      {
        dataIndex: "created",
        key: "created",
        title: this.props.i18n.t("Created"),
        render: (val, record) => (
          <span>
            {moment
              .utc(val)
              .tz(
                this.props.userTimezone ||
                  get(record, "site.time_zone") ||
                  "UTC"
              )
              .format("YYYY-MM-DD HH:mm")}{" "}
            {this.props.userShortTimezone ||
              get(record, "site.short_tz") ||
              "UTC"}
          </span>
        ),
      },
      {
        dataIndex: "scheduled_date",
        key: "scheduled_date",
        title: this.props.i18n.t("NamespaceAdminDashboard:Scheduled date"),
        render: (val, record) => (
          <span>
            {moment
              .utc(val)
              .tz(
                this.props.userTimezone ||
                  get(record, "site.time_zone") ||
                  "UTC"
              )
              .format("YYYY-MM-DD HH:mm")}{" "}
            {this.props.userShortTimezone ||
              get(record, "site.short_tz") ||
              "UTC"}
          </span>
        ),
      },
      {
        dataIndex: "subject",
        key: "subject_action",
        title: this.props.i18n.t("Actions"),
        render: (val) => (
          <div>
            <Link
              to={withParams(routes.admin.patientProfileSingle, {
                subjectID: get(val, "code"),
                followup_type: this.state.followup_type,
              })}
            >
              <Icon type="eye" theme="filled" className={classes.viewIcon} />
            </Link>
          </div>
        ),
      },
    ];
  }

  handleFilterChange = (value) => {
    if (value === "month") {
      this.setState({ dateRange: [moment(), moment().add(30, "days")] });
    }

    this.setState({ filter: value, site: null }, this.getDashboardData);
  };

  handleSiteChange = (value) => {
    this.setState({ site: value }, this.getDashboardData);
  };

  getDashboardData = async () => {
    this.setState({ isLoadingFollowUps: true });
    try {
      let dateRangeParams = {};

      if (this.state.filter === "month") {
        const { dateRange } = this.state;
        dateRangeParams = {
          start_date: moment(
            moment(dateRange[0]).format("YYYY-MM-DDT00:00")
          ).toISOString(),
          end_date: moment(
            moment(dateRange[1]).add(1, "day").format("YYYY-MM-DDT00:00")
          ).toISOString(),
        };
      }

      const { data } = await getDashboardRequest({
        filter: this.state.filter,
        siteCode: this.state.site,
        currency: this.state.currency,
        ...dateRangeParams,
      });

      if (this.props.consentAccessPermission) {
        const { data: consentData } = await getDashboardConsentRequest();
        this.setState({ dashboard: data, consentData });
      } else {
        this.setState({ dashboard: data });
      }
    } catch (e) {
      if (get(e, "response.status") === 401) {
        this.props.logout();
        return;
      }
      console.log(e);
    }

    this.setState({ isLoadingFollowUps: false });
  };

  getRandomizationConfig = async () => {
    const { data: randomizationConfig } = await getRandomizationConfig();
    const { value, type } = get(randomizationConfig, "follow_ups[0]", {});
    this.setState(
      {
        followUpsConfig: get(randomizationConfig, "follow_ups", []),
        followup_filter: "upcoming_not_due",
        followup_type: `follow_up_${value}_${type}`,
      }
    );
  };

  getFollowUps = async (page = 1, per_page = this.state.pageSize) => {
    try {
      const { data } = await getFollowUpsRequest({
        filter_by: this.state.followup_filter,
        type: this.state.followup_type,
        page,
        per_page,
      });
      this.setState({
        followups: get(data, "data"),
        total: get(data, "total_pages") * per_page,
        currentPage: page,
      });
    } catch (e) {
      if (get(e, "response.status") === 401) {
        this.props.logout();
        return;
      }
      console.log(e);
    }
  };

  setFollowUpFilter = (filter) => {
    this.setState(
      {
        followup_filter: filter.split("__")[0],
        followup_type: filter.split("__")[1],
        is_30_days: filter.includes("_30_"),
      },
      this.getFollowUps
    );
  };

  handleTableChange = (pagination) => {
    this.setState(
      {
        pageSize: pagination.pageSize,
      },
      () => this.getFollowUps(pagination.current, pagination.pageSize)
    );
  };

  getEnrolmentsLabels = () => {
    const { filter } = this.state;
    const enrolments = get(this.state.dashboard, "enrolments");

    return Array.isArray(enrolments)
      ? enrolments.map((item) => {
          if (filter === "site") {
            return get(item, "site");
          }
          if (filter === "month") {
            return get(item, "year_month");
          }
        })
      : [this.props.i18n.t("All sites")];
  };

  getEnrolmentsDatasets = () => {
    const dataSet = [
      {
        label: "Total subjects",
        ...colors.blue,
      },
    ];
    const enrolments = get(this.state.dashboard, "enrolments", []);

    if (Array.isArray(enrolments)) {
      dataSet[0].data = get(
        this.state.dashboard,
        "enrolments",
        []
      ).map((item) => get(item, "count"));
    } else {
      dataSet[0].data = [enrolments];
    }
    return dataSet;
  };

  getDrugAvailabilityLabels = () => {
    const { filter } = this.state;

    const drugAvailabilityCount = get(
      this.state.dashboard,
      "drug_availability.total"
    );

    return Array.isArray(drugAvailabilityCount)
      ? drugAvailabilityCount.map((item) => {
          if (filter === "site") {
            return get(item, "site");
          }
          if (filter === "month") {
            return get(item, "year_month");
          }
        })
      : ["All sites"];
  };

  getDrugAvailabilityDatasets = () => {
    const dataSet = [
      {
        label: this.props.i18n.t(
          "NamespaceAdminDashboard:Total drugs ids received"
        ),
        ...colors.gray,
      },
      {
        label: this.props.i18n.t("NamespaceAdminDashboard:In transit"),
        ...colors.blueGreen,
      },
      { label: this.props.i18n.t("Available"), ...colors.blueLight },
      { label: this.props.i18n.t("Allocated"), ...colors.green },
      { label: this.props.i18n.t("Quarantined"), ...colors.violet },
      {
        label: this.props.i18n.t("NamespaceAdminDashboard:Sent back"),
        ...colors.orange,
      },
      { label: this.props.i18n.t("Lost"), ...colors.greenDark },
      {
        label: this.props.i18n.t(
          "NamespaceAdminDashboard:Permanently Unavailable"
        ),
        ...colors.orange,
      },
      {
        label: this.props.i18n.t("NamespaceAdminDashboard:Returned/Destroyed"),
        ...colors.red,
      },
      {
        label: this.props.i18n.t("NamespaceAdminDashboard:Replaced"),
        ...colors.sun,
      },
      {
        label: this.props.i18n.t("NamespaceAdminDashboard:Expired"),
        ...colors.red,
      },
    ];

    const es1 = get(this.state.dashboard, "drug_availability.total");
    const es2 = get(this.state.dashboard, "drug_availability.in_transit");
    const es3 = get(
      this.state.dashboard,
      "drug_availability.in_pharmacy_and_fridges"
    );
    const es4 = get(this.state.dashboard, "drug_availability.allocated");
    const es5 = get(this.state.dashboard, "drug_availability.quarantined");
    const es6 = get(this.state.dashboard, "drug_availability.sent_back");
    const es7 = get(this.state.dashboard, "drug_availability.lost");
    const es8 = get(this.state.dashboard, "drug_availability.not_available");
    const es9 = get(this.state.dashboard, "drug_availability.returned");
    const es10 = get(this.state.dashboard, "drug_availability.replaced");
    const es11 = get(this.state.dashboard, "drug_availability.expired");

    dataSet[0].data = Array.isArray(es1)
      ? es1.map((item) => get(item, "count"))
      : [es1];
    dataSet[1].data = Array.isArray(es2)
      ? es2.map((item) => get(item, "count"))
      : [es2];
    dataSet[2].data = Array.isArray(es3)
      ? es3.map((item) => get(item, "count"))
      : [es3];
    dataSet[3].data = Array.isArray(es4)
      ? es4.map((item) => get(item, "count"))
      : [es4];
    dataSet[4].data = Array.isArray(es5)
      ? es5.map((item) => get(item, "count"))
      : [es5];
    dataSet[5].data = Array.isArray(es6)
      ? es6.map((item) => get(item, "count"))
      : [es6];
    dataSet[6].data = Array.isArray(es7)
      ? es7.map((item) => get(item, "count"))
      : [es7];
    dataSet[7].data = Array.isArray(es8)
      ? es8.map((item) => get(item, "count"))
      : [es8];
    dataSet[8].data = Array.isArray(es9)
      ? es9.map((item) => get(item, "count"))
      : [es9];
    dataSet[9].data = Array.isArray(es10)
      ? es10.map((item) => get(item, "count"))
      : [es10];
    dataSet[10].data = Array.isArray(es11)
      ? es11.map((item) => get(item, "count"))
      : [es11];

    return dataSet;
  };

  getEnrolmentsStageLabels = () => {
    const { filter } = this.state;

    const enrolmentStagesRandomize = get(
      this.state.dashboard,
      "enrolment_stages.randomize"
    );

    return Array.isArray(enrolmentStagesRandomize)
      ? enrolmentStagesRandomize.map((item) => {
          if (filter === "site") {
            return get(item, "site");
          }
          if (filter === "month") {
            return get(item, "year_month");
          }
        })
      : ["All sites"];
  };

  getEnrolmentsStageDatasets = () => {
    const dataSet = [
      { label: this.props.i18n.t("Profile"), ...colors.violet },
      { label: this.props.i18n.t("Randomize"), ...colors.greenDark },

      ...map(
        Object.keys(
          get(this.state.dashboard, "enrolment_stages", {})
        ).filter((key) => key.includes("follow_up")),
        (key, i) => {
          return {
            label: capitalize(
              key.replace("_conducted_count", "").replace(/_/g, " ")
            ),
            ...[
              colors.yellow,
              colors.blue,
              colors.greenDark,
              colors.blueGreen,
              colors.orange5,
              colors.blueLight,
            ][i % 6],
          };
        }
      ),
    ];

    const esProfile = get(this.state.dashboard, "enrolment_stages.profile");
    const esRandomize = get(this.state.dashboard, "enrolment_stages.randomize");

    dataSet[0].data = Array.isArray(esProfile)
      ? esProfile.map((item) => get(item, "count"))
      : [esProfile];
    dataSet[1].data = Array.isArray(esRandomize)
      ? esRandomize.map((item) => get(item, "count"))
      : [esRandomize];
    // dataSet[10].data = Array.isArray(esDeath) ? esDeath.map(item => get(item, 'count')) : [esDeath];

    forEach(
      Object.keys(
        get(this.state.dashboard, "enrolment_stages", {})
      ).filter((key) => key.includes("follow_up")),
      (key, i) => {
        dataSet[i + 2].data = [
          get(this.state.dashboard, `enrolment_stages.${key}`),
        ];
      }
    );

    if (this.props.isDrugManagementActive) {
      const esDrug = get(this.state.dashboard, "enrolment_stages.drug");
      dataSet.push({
        label: this.props.i18n.t("Drug administered"),
        ...colors.blueGreen,
      });

      dataSet[dataSet.length - 1].data = Array.isArray(esDrug)
        ? esDrug.map((item) => get(item, "count"))
        : [esDrug];
    }

    return dataSet;
  };

  getInvoiceCountLabels = () => {
    const { filter } = this.state;

    const invoiceItem = get(this.state.dashboard, "invoices.draft");

    return Array.isArray(invoiceItem)
      ? invoiceItem.map((item) => {
          if (filter === "site") {
            return get(item, "site");
          }
          if (filter === "month") {
            return get(item, "year_month");
          }
        })
      : [this.props.i18n.t("All sites")];
  };

  getInvoiceCountDatasets = () => {
    const dataSet = [
      { label: this.props.i18n.t("Draft"), ...colors.blue },
      { label: this.props.i18n.t("Sent"), ...colors.yellow },
      { label: this.props.i18n.t("Overdue"), ...colors.red },
      { label: this.props.i18n.t("Cancelled"), ...colors.gray },
      { label: this.props.i18n.t("Paid"), ...colors.green },
    ];

    ["draft", "sent", "overdue", "cancelled", "paid"].forEach((key, i) => {
      const item = get(this.state.dashboard, `invoices.${key}`);
      dataSet[i].data = Array.isArray(item)
        ? item.map((item) => get(item, "count"))
        : [item];
    });

    return dataSet;
  };

  getConsentCountLabels = () => {
    // const data = Object.keys(this.state.consentData)
    //   .filter((key) => key !== "email_consent_total")
    //   .map((item) => (item ? item.replace(/_/g, " ") : ""));

    return [this.props.i18n.t("Consents")];
  };

  getConsentCountDatasets = () => {
    const dataSet = Object.keys(this.state.consentData)
      .filter((key) => key !== "email_consent_total")
      .map((item, i) => ({
        label: item ? item.replace(/_/g, " ") : "",
        data: [this.state.consentData[item]],
        ...[
          colors.blue,
          colors.yellow,
          colors.green,
          colors.red,
          colors.gray,
          colors.orange,
        ][i % 6],
      }));

    return dataSet;
  };

  getConsentEmailCountLabels = () => {
    // return Object.keys(
    //   get(this.state.consentData, "email_consent_total", {})
    // ).map((item) => (item ? item.replace(/_/g, " ") : ""));

    return [this.props.i18n.t("Consents")];
  };

  getConsentEmailCountDatasets = () => {
    const dataSet = Object.keys(
      get(this.state.consentData, "email_consent_total", {})
    )
      .filter((key) => key !== "email_consent_total")
      .map((item, i) => ({
        label: item ? item.replace(/_/g, " ") : "",
        data: [get(this.state.consentData, "email_consent_total", {})[item]],
        ...[
          colors.blueLight,
          colors.orange,
          colors.red,
          colors.green,
        ][i % 4],
      }));
    return dataSet;
  };

  getInvoiceAmountLabels = () => {
    const { filter } = this.state;

    const invoiceItem = get(this.state.dashboard, "invoices_amount.draft");

    return Array.isArray(invoiceItem)
      ? invoiceItem.map((item) => {
          if (filter === "site") {
            return get(item, "site");
          }
          if (filter === "month") {
            return get(item, "year_month");
          }
        })
      : [this.props.i18n.t("All sites")];
  };

  getInvoiceAmountDatasets = () => {
    const dataSet = [
      { label: this.props.i18n.t("Draft"), ...colors.blue },
      { label: this.props.i18n.t("Sent"), ...colors.yellow },
      { label: this.props.i18n.t("Overdue"), ...colors.red },
      { label: this.props.i18n.t("Cancelled"), ...colors.gray },
      { label: this.props.i18n.t("Paid"), ...colors.green },
    ];

    ["draft", "sent", "overdue", "cancelled", "paid"].forEach((key, i) => {
      const item = get(this.state.dashboard, `invoices_amount.${key}`);
      dataSet[i].data = Array.isArray(item)
        ? item.map((item) => get(item, "count"))
        : [item];
    });

    return dataSet;
  };

  onDateRangeChange = (val) => {
    this.setState({ dateRange: val }, this.getDashboardData);
  };

  expandedRowRender = (record) => (
    <div>
      {get(record, "subject.profile") ? (
        <Row gutter={8}>
          <Col lg={6} md={12}>
            <h5 className="headline-default">
              {this.props.i18n.t("NamespaceAdminDashboard:Patient info")}
            </h5>
            <p>
              <b>{this.props.i18n.t("Name")}:</b>{" "}
              {get(record, "subject.profile.first_name")}{" "}
              {get(record, "subject.profile.last_name")}
            </p>
            <p>
              <b>{this.props.i18n.t("Email")}:</b>{" "}
              {get(record, "subject.profile.email")}
            </p>
            <p>
              <b>{this.props.i18n.t("Phone")}:</b>{" "}
              {get(record, "subject.profile.phone_number_type_1")}{" "}
              {get(record, "subject.profile.phone_number_1")}
            </p>
            <p>
              <b>{this.props.i18n.t("NamespaceAdminDashboard:Address")}:</b>{" "}
              {get(record, "subject.profile.street_address")}{" "}
              {get(record, "subject.profile.city")},{" "}
              {get(record, "subject.profile.postal_code")}
            </p>
            <p>
              <b>{this.props.i18n.t("NamespaceAdminDashboard:Is deceased")}:</b>{" "}
              {get(record, "subject.profile.is_deceased")
                ? this.props.i18n.t("Yes")
                : this.props.i18n.t("No")}
            </p>
          </Col>

          <Col lg={6} md={12}>
            <h5 className="headline-default">
              {this.props.i18n.t("NamespaceAdminDashboard:Other contact")}
            </h5>
            <p>
              <b>{this.props.i18n.t("Name")}:</b>{" "}
              {get(record, "subject.profile.contact_1_first_name")}{" "}
              {get(record, "subject.profile.contact_1_last_name")}
            </p>
            <p>
              <b>{this.props.i18n.t("Relationship")}:</b>{" "}
              {get(
                record,
                "subject.profile.contact_1_relationship_type"
              ).replace(/_/g, " ")}
            </p>
            <p>
              <b>{this.props.i18n.t("Email")}:</b>{" "}
              {get(record, "subject.profile.contact_1_email")}
            </p>
            <p>
              <b>{this.props.i18n.t("Phone")}:</b>{" "}
              {get(record, "subject.profile.contact_1_phone_number_type")}{" "}
              {get(record, "subject.profile.contact_1_phone_number")}
            </p>
          </Col>

          {get(record, "subject.profile.contact_2_first_name") && (
            <Col lg={6} md={12}>
              <h5 className="headline-default">
                {this.props.i18n.t("NamespaceAdminDashboard:Other contact 2")}
              </h5>
              <p>
                <b>{this.props.i18n.t("Name")}:</b>{" "}
                {get(record, "subject.profile.contact_2_first_name")}{" "}
                {get(record, "subject.profile.contact_2_last_name")}
              </p>
              <p>
                <b>{this.props.i18n.t("Relationship")}:</b>{" "}
                {get(
                  record,
                  "subject.profile.contact_2_relationship_type"
                ).replace(/_/g, " ")}
              </p>
              <p>
                <b>{this.props.i18n.t("Email")}:</b>{" "}
                {get(record, "subject.profile.contact_2_email")}
              </p>
              <p>
                <b>{this.props.i18n.t("Phone")}:</b>{" "}
                {get(record, "subject.profile.contact_2_phone_number_type")}{" "}
                {get(record, "subject.profile.contact_2_phone_number")}
              </p>
            </Col>
          )}

          <Col lg={6} md={12}>
            <h5 className="headline-default">
              {this.props.i18n.t("NamespaceAdminDashboard:Family Doctor")}
            </h5>
            <p>
              <b>{this.props.i18n.t("Name")}:</b>{" "}
              {get(record, "subject.profile.family_doctor_first_name")}{" "}
              {get(record, "subject.profile.family_doctor_last_name")}
            </p>
            <p>
              <b>{this.props.i18n.t("NamespaceAdminDashboard:Clinic")}:</b>{" "}
              {get(record, "subject.profile.family_doctor_clinic_name")}
            </p>
            <p>
              <b>{this.props.i18n.t("Phone")}:</b>{" "}
              {get(record, "subject.profile.family_doctor_phone_number_type")}{" "}
              {get(record, "subject.profile.family_doctor_phone_number")}
            </p>
          </Col>
        </Row>
      ) : (
        <div>
          {this.props.i18n.t("NamespaceAdminDashboard:Profile is empty")}
        </div>
      )}
    </div>
  );

  customExpandIcon(props) {
    if (props.expanded) {
      return (
        <a
          style={{ color: "black" }}
          onClick={(e) => {
            props.onExpand(props.record, e);
          }}
        >
          <Icon
            type="eye-invisible"
            theme="filled"
            className={classes.viewIcon}
          />
        </a>
      );
    } else {
      return (
        <a
          style={{ color: "black" }}
          onClick={(e) => {
            props.onExpand(props.record, e);
          }}
        >
          <Icon type="eye" theme="filled" className={classes.viewIcon} />
        </a>
      );
    }
  }

  getFollowUpsSummaryDataset = () => {
    const { dashboard, followUpsConfig } = this.state;
    const followUpsSummaryDataset = [];

    forEach(followUpsConfig, ({ type, value, name, slug, track_on_dashboard}) => {
      if (track_on_dashboard){
        followUpsSummaryDataset.push({
          label: this.props.i18n.t(
            "NamespaceAdminDashboard:Followup not due") + ` ${name}`
          ,
          ...colors.gray,
          data: [
            get(
              dashboard,
              `summary_bi_report.follow_up.${slug}.not_due`,
              0
            ),
          ],
        });
        followUpsSummaryDataset.push({
          label: this.props.i18n.t(
            "NamespaceAdminDashboard:Follow-ups due") + ` ${name}`
          ,
          ...colors.yellow,
          data: [
            get(
              dashboard,
              `summary_bi_report.follow_up.${slug}.follow_ups_due`,
              0
            ),
          ],
        });
        followUpsSummaryDataset.push({
          label: this.props.i18n.t(
            "NamespaceAdminDashboard:Overdue follow-ups") + ` ${name}`
          ,
          ...colors.red,
          data: [
            get(
              dashboard,
              `summary_bi_report.follow_up.${slug}.follow_ups_overdue`,
              0
            ),
          ],
        });
        followUpsSummaryDataset.push({
          label: this.props.i18n.t(
            "NamespaceAdminDashboard:Followup completed") + ` ${name}`
          ,
          ...colors.green,
          data: [
            get(
              dashboard,
              `summary_bi_report.follow_up.${slug}.completed`,
              0
            ),
          ],
        });
      }
    });

    return followUpsSummaryDataset;
  };

  render() {
    const { isDrugManagementActive, isProjectConfigurationActive } = this.props;
    const {
      dashboard,
      filter,
      sites,
      dateRange,
      followups,
      total,
      pageSize,
      currentPage,
      followup_filter,
      followup_type,
      isLoadingFollowUps,
      currency,
      followUpsConfig,
    } = this.state;
    const progressPar = get(dashboard, "progress_bar");
    const report30days = get(
      dashboard,
      "summary_bi_report.follow_up_next_30_days_records",
      null
    );

    return (
      <div className={classes.wrapper}>
        <Helmet>
          <title>
            {this.props.i18n.t("Trial Management Portal")}:{" "}
            {this.props.i18n.t("Dashboard")}
          </title>
        </Helmet>
        <div className={classes.parentContainer}>
          {isProjectConfigurationActive ? (
            <div className={classes.content}>
              <h4 className="headline-default">
                {this.props.i18n.t("NamespaceAdminDashboard:Study progress")}
              </h4>
              {get(progressPar, "target_count") && (
                <p>
                  <b>
                    {this.props.i18n.t("NamespaceAdminDashboard:Target count")}:{" "}
                    {get(progressPar, "target_count", 0)}
                  </b>
                </p>
              )}
              <Row className="contentMb-24" gutter={24}>
                {/*<Col md={12}>
                        <b>Follow up 7 days completed</b>
                        <br/>
                        <Progress percent={+(100 * get(progressPar, 'follow_up_7_days_completed_count', 0) / get(progressPar, 'target_count', 1)).toFixed(3)} strokeColor='#74B584' />

                        <b>Follow up 7 days not Completed</b>
                        <br/>
                        <Progress percent={+(100 * get(progressPar, 'follow_up_7_days_not_completed_count', 0) / get(progressPar, 'target_count', 1)).toFixed(3)} strokeColor='#F2A788' />
                      </Col>

                      <Col md={12}>
                        <b>Follow up 30 days completed</b>
                        <br/>
                        <Progress percent={+(100 * get(progressPar, 'follow_up_30_days_completed_count', 0) / get(progressPar, 'target_count', 1)).toFixed(3)} strokeColor='#74B584' />

                        <b>Follow up 30 days not Completed</b>
                        <br/>
                        <Progress percent={+(100 * get(progressPar, 'follow_up_30_days_not_completed_count', 0) / get(progressPar, 'target_count', 1)).toFixed(3)} strokeColor='#F2A788' />
                      </Col>*/}

                <Col md={24}>
                  <b>
                    {this.props.i18n.t(
                      "NamespaceAdminDashboard:Randomization completed"
                    )}
                  </b>
                  <br />
                  <Progress
                    percent={
                      +(
                        (100 *
                          get(
                            progressPar,
                            "randomization_completed_count",
                            0
                          )) /
                        get(progressPar, "target_count", 1)
                      ).toFixed(3)
                    }
                    strokeColor="#74B584"
                  />
                </Col>
              </Row>

              {this.props.dashboardPermission && (
                <div>
                  <div>
                    {this.props.dashboardReadFollowupPermission && (
                      <Row>
                        <Col md={24}>
                          <h5 className="headline-default">
                            {this.props.i18n.t(
                              "NamespaceAdminDashboard:Follow-ups Summary"
                            )}
                          </h5>
                          <div>
                            {get(this.state, "followUpsConfig") && <HorizontalBar
                              data={{
                                labels: [""],
                                datasets: this.getFollowUpsSummaryDataset(),
                              }}
                              width={100}
                              // height={550}

                              height={
                                (get(this.state, "followUpsConfig", []).length * 5 ) < 12
                                  ? 450
                                  : (get(this.state, "followUpsConfig", []).length * 5) * 18
                              }
                              options={{
                                maintainAspectRatio: false,
                                type: "horizontalBar",
                                scales: {
                                  xAxes: [
                                    {
                                      ticks: {
                                        beginAtZero: true,
                                        stepSize: 1,
                                      },
                                      gridLines: { color: "#eee" },
                                    },
                                  ],
                                  yAxes: [
                                    {
                                      gridLines: { color: "#eee" },
                                    },
                                  ],
                                },
                              }}
                              type="horizontalBar"
                            />}
                          </div>
                        </Col>
                      </Row>
                    )}

                    {this.props.dashboardReadFollowupPermission && (
                      <div>
                        <h4 className="headline-default">
                          {this.props.i18n.t(
                            "NamespaceAdminDashboard:Upcoming follow-ups"
                          )}
                        </h4>

                        <div>
                          <label
                            style={{ maxWidth: "300px", display: "block" }}
                          >
                            <b>{this.props.i18n.t("Filter")}:</b>
                            <br />
                            <Select
                              onChange={this.setFollowUpFilter}
                              style={{ width: "100%" }}
                              placeholder={this.props.i18n.t("Select")}
                            >
                              {map(followUpsConfig, ({ type, value, name, slug, track_on_dashboard}, i) =>
                                (track_on_dashboard && map(
                                  [
                                    {
                                      interval: "upcoming_not_due",
                                      label: this.props.i18n.t(
                                        "NamespaceAdminDashboard:Upcoming not due"
                                      ),
                                    },
                                    {
                                      interval: "follow_ups_due",
                                      label: this.props.i18n.t(
                                        "NamespaceAdminDashboard:Followups due"
                                      ),
                                    },
                                    {
                                      interval: "follow_ups_overdue",
                                      label: this.props.i18n.t(
                                        "NamespaceAdminDashboard:Followups overdue"
                                      ),
                                    },
                                    {
                                      interval: "follow_ups_completed",
                                      label: this.props.i18n.t(
                                        "NamespaceAdminDashboard:Followups completed"
                                      ),
                                    },
                                  ],
                                  ({ interval, label }, k) => (
                                    <Option
                                      key={`${i}_${k}`}
                                      value={`${interval}__${slug}`}
                                    >
                                      {label}
                                      {`: ${name}`}
                                    </Option>
                                  )
                                ))
                              )}
                            </Select>
                          </label>
                        </div>
                        <br />

                        <Table
                          className={classes.tableWrapper}
                          columns={this.columns()}
                          dataSource={followups}
                          loading={isLoadingFollowUps}
                          onChange={this.handleTableChange}
                          // expandedRowRender={this.expandedRowRender}
                          // expandIcon={(props) => this.customExpandIcon(props)}
                          pagination={{
                            onShowSizeChange: (current, size) => {
                              this.setState({ pageSize: size });
                            },
                            current: currentPage,
                            total,
                            pageSize,
                            showSizeChanger: true,
                            pageSizeOptions: ["10", "25", "50", "100"],
                            position: 'both'
                          }}
                        />
                        <br />
                      </div>
                    )}
                  </div>

                  {(this.props.dashboardReadEnrolmentBySitePermission ||
                    this.props.dashboardEnrolmentStagesPermission ||
                    this.props.dashboardReadSAEPermission ||
                    this.props.dashboardReadDeviationPermission) && (
                    <Row gutter={12}>
                      <Col md={12} lg={6}>
                        <b>{this.props.i18n.t("Filter")}:</b>
                        <br />
                        <Select
                          value={filter}
                          onChange={this.handleFilterChange}
                          style={{ width: "100%" }}
                          placeholder={this.props.i18n.t("Select")}
                        >
                          <Option value="site">
                            {this.props.i18n.t(
                              "NamespaceAdminDashboard:By Site"
                            )}
                          </Option>
                          <Option value="month">
                            {this.props.i18n.t(
                              "NamespaceAdminDashboard:By Date Range"
                            )}
                          </Option>
                          <Option value="">{this.props.i18n.t("None")}</Option>
                        </Select>
                        <br />
                        <br />
                      </Col>
                      {filter === "site" && (
                        <Col md={12} lg={6}>
                          <b>{this.props.i18n.t("Site")}:</b>
                          <br />
                          <Select
                            onChange={this.handleSiteChange}
                            style={{ width: "100%" }}
                            placeholder={this.props.i18n.t("Select")}
                          >
                            {sites
                              .filter(
                                (site) =>
                                  (get(site, "site_type") === "randomization" ||
                                  get(site, "site_type") === "depot") && get(site, "state") === "active"
                              )
                              .map((site) => (
                                <Option key={site.id} value={site.code}>
                                  {site.name}
                                </Option>
                              ))}
                            <Option value="">
                              {this.props.i18n.t("All sites")}
                            </Option>
                          </Select>
                        </Col>
                      )}
                      {filter === "month" && (
                        <Col md={12} lg={6}>
                          <b>{this.props.i18n.t("Select date range")}</b>
                          <br />
                          <RangePicker
                            value={dateRange}
                            onChange={this.onDateRangeChange}
                            format="DD MMM, YYYY"
                            allowClear={false}
                          />
                        </Col>
                      )}
                    </Row>
                  )}

                  <Row>
                    {this.props.dashboardReadEnrolmentBySitePermission && (
                      <Col md={12}>
                        <h5 className="headline-default">
                          {this.props.i18n.t(
                            "NamespaceAdminDashboard:Enrolment by Site"
                          )}
                        </h5>
                        <div>
                          <Bar
                            data={{
                              labels: this.getEnrolmentsLabels(),
                              datasets: this.getEnrolmentsDatasets(),
                            }}
                            width={100}
                            height={300}
                            options={{
                              maintainAspectRatio: false,
                              scales: {
                                yAxes: [
                                  {
                                    ticks: { beginAtZero: true, stepSize: 1 },
                                    gridLines: { color: "#eee" },
                                  },
                                ],
                                xAxes: [
                                  {
                                    gridLines: { color: "#eee" },
                                  },
                                ],
                              },
                            }}
                          />
                        </div>
                      </Col>
                    )}

                    {this.props.dashboardEnrolmentStagesPermission &&
                      dashboard && (
                        <Col md={12}>
                          <h5 className="headline-default">
                            {this.props.i18n.t(
                              "NamespaceAdminDashboard:Enrolment Stage"
                            )}
                          </h5>
                          <div>
                            <HorizontalBar
                              data={{
                                labels: this.getEnrolmentsStageLabels(),
                                datasets: this.getEnrolmentsStageDatasets(),
                              }}
                              width={100}
                              height={300}
                              options={{
                                maintainAspectRatio: false,
                                type: "horizontalBar",
                                scales: {
                                  xAxes: [
                                    {
                                      ticks: { beginAtZero: true, stepSize: 1 },
                                      gridLines: { color: "#eee" },
                                    },
                                  ],
                                  yAxes: [{ gridLines: { color: "#eee" } }],
                                },
                              }}
                              type="horizontalBar"
                            />
                          </div>
                        </Col>
                      )}

                    {/*{this.props.dashboardReadSAEPermission && (<Col md={12}>
                          <h5 className="headline-default">SAEs</h5>
                          <div>
                            <HorizontalBar  data={{
                              labels: this.getSaesLabels(),
                              datasets: this.getSaesDatasets()
                            }}
                            width={100}
                            height={300}
                            options={{ maintainAspectRatio: false, type: 'horizontalBar', scales: { xAxes: [{ticks: {beginAtZero: true, stepSize: 1}}] } }}
                            type='horizontalBar' />
                          </div>
                        </Col>)}*/}

                    {this.props.dashboardDrugAvailabilityPermission &&
                      isDrugManagementActive && (
                        <Col md={12}>
                          <h5 className="headline-default">
                            {this.props.i18n.t(
                              "NamespaceAdminDashboard:Drug Availability"
                            )}
                          </h5>
                          <div>
                            <HorizontalBar
                              data={{
                                labels: this.getDrugAvailabilityLabels(),
                                datasets: this.getDrugAvailabilityDatasets(),
                              }}
                              width={100}
                              height={400}
                              options={{
                                maintainAspectRatio: false,
                                type: "horizontalBar",
                                scales: {
                                  xAxes: [
                                    {
                                      ticks: {
                                        beginAtZero: true,
                                        stepSize: 1,
                                      },
                                      gridLines: { color: "#eee" },
                                    },
                                  ],
                                  yAxes: [
                                    {
                                      gridLines: { color: "#eee" },
                                    },
                                  ],
                                },
                              }}
                              type="horizontalBar"
                            />
                          </div>
                        </Col>
                      )}
                  </Row>

                  {this.props.dashboardInvoiceAmountPermission && (
                    <Row>
                      <Col>
                        <br />
                        <br />
                        <b>
                          {this.props.i18n.t(
                            "NamespaceAdminDashboard:Select Currency"
                          )}
                          :
                        </b>
                        <br />
                        <Select
                          value={currency}
                          onChange={(currency) => {
                            this.setState({ currency }, this.getDashboardData);
                          }}
                          style={{ maxWidth: "500px", width: "100%" }}
                          placeholder={this.props.i18n.t("Select")}
                        >
                          <Option value="CAD">CAD</Option>
                          <Option value="USD">USD</Option>
                          <Option value="EUR">EUR</Option>
                          <Option value="AUD">AUD</Option>
                        </Select>
                        <br />
                        <br />
                      </Col>
                    </Row>
                  )}

                  <Row>
                    {this.props.dashboardInvoiceCountPermission && (
                      <Col md={12}>
                        <h5 className="headline-default">
                          {this.props.i18n.t("Invoices")}
                        </h5>
                        <div>
                          <HorizontalBar
                            data={{
                              labels: this.getInvoiceCountLabels(),
                              datasets: this.getInvoiceCountDatasets(),
                            }}
                            width={100}
                            height={400}
                            options={{
                              maintainAspectRatio: false,
                              type: "horizontalBar",
                              scales: {
                                xAxes: [
                                  {
                                    ticks: { beginAtZero: true },
                                    gridLines: { color: "#eee" },
                                  },
                                ],
                                yAxes: [
                                  {
                                    gridLines: { color: "#eee" },
                                  },
                                ],
                              },
                            }}
                            type="horizontalBar"
                          />
                        </div>
                      </Col>
                    )}
                    {this.props.dashboardInvoiceAmountPermission && (
                      <Col md={12}>
                        <h5 className="headline-default">
                          {this.props.i18n.t(
                            "NamespaceAdminDashboard:Invoices amount"
                          )}
                        </h5>
                        <div>
                          <HorizontalBar
                            data={{
                              labels: this.getInvoiceAmountLabels(),
                              datasets: this.getInvoiceAmountDatasets(),
                            }}
                            width={100}
                            height={400}
                            options={{
                              maintainAspectRatio: false,
                              type: "horizontalBar",
                              scales: {
                                xAxes: [
                                  {
                                    ticks: { beginAtZero: true },
                                    gridLines: { color: "#eee" },
                                  },
                                ],
                              },
                              tooltips: {
                                callbacks: {
                                  label: function (tooltipItem) {
                                    return `${(+tooltipItem.value)
                                      .toFixed(2)
                                      .replace(
                                        /\d(?=(\d{3})+\.)/g,
                                        `$&,`
                                      )} ${currency}`;
                                  },
                                },
                              },
                            }}
                            type="horizontalBar"
                          />
                        </div>
                      </Col>
                    )}
                  </Row>

                  {this.props.consentAccessPermission && <Row>
                    <Col md={12}>
                      <br/>
                      <h5 className="headline-default">
                        {this.props.i18n.t("Consent")}
                      </h5>
                      <div>
                        <HorizontalBar
                          data={{
                            labels: this.getConsentCountLabels(),
                            datasets: this.getConsentCountDatasets(),
                          }}
                          width={100}
                          height={400}
                          options={{
                            maintainAspectRatio: false,
                            type: "horizontalBar",
                            scales: {
                              xAxes: [
                                {
                                  ticks: { beginAtZero: true, stepSize: 1 },
                                  gridLines: { color: "#eee" },
                                },
                              ],
                              yAxes: [
                                {
                                  gridLines: { color: "#eee" },
                                },
                              ],
                            },
                          }}
                          type="horizontalBar"
                        />
                      </div>
                    </Col>
                    <Col md={12}>
                      <br/>
                      <h5 className="headline-default">
                        {this.props.i18n.t("Email consent total\n")}
                      </h5>
                      <div>
                        <HorizontalBar
                          data={{
                            labels: this.getConsentEmailCountLabels(),
                            datasets: this.getConsentEmailCountDatasets(),
                          }}
                          width={100}
                          height={400}
                          options={{
                            maintainAspectRatio: false,
                            type: "horizontalBar",
                            scales: {
                              xAxes: [
                                {
                                  ticks: { beginAtZero: true, stepSize: 1 },
                                  gridLines: { color: "#eee" },
                                },
                              ],
                              yAxes: [
                                {
                                  gridLines: { color: "#eee" },
                                },
                              ],
                            },
                          }}
                          type="horizontalBar"
                        />
                      </div>
                    </Col>
                  </Row>}
                </div>
              )}
            </div>
          ) : (
            <div className={classes.content}>
              <Link
                to={routes.admin.wizardConfig}
                className="ant-dropdown-link"
              >
                <Button>
                  {this.props.i18n.t("Start Project Configuration")}
                </Button>
              </Link>
            </div>
          )}
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  userTimezone: getUserTimezone(state),
  userShortTimezone: getUserShortTimezone(state),
  dashboardPermission: getDashboardPermission(state),
  dashboardFollowUp7dPermission: getDashboardFollowUp7dPermission(state),
  dashboardReadEnrolmentBySitePermission: getDashboardReadEnrolmentBySitePermission(
    state
  ),
  dashboardEnrolmentStagesPermission: getDashboardEnrolmentStagesPermission(
    state
  ),
  dashboardReadSAEPermission: getDashboardReadSAEPermission(state),
  dashboardReadDeviationPermission: getDashboardReadDeviationPermission(state),
  dashboardDrugAvailabilityPermission: getDashboardDrugAvailabilityPermission(
    state
  ),
  dashboardInvoiceCountPermission: getDashboardInvoiceCountPermission(state),
  dashboardInvoiceAmountPermission: getDashboardInvoiceAmountPermission(state),
  isDrugManagementActive: getDrugManagementStatus(state),
  isProjectConfigurationActive: getProjectConfigurationStatus(state),
  dashboardReadFollowupPermission: getDashboardReadFollowupPermission(state),
  consentAccessPermission: getViewConsentDashboardChartsPermission(state),
});

const mapDispatchToProps = { logout };

export default isAuthenticated(
  connect(mapStateToProps, mapDispatchToProps)(withNamespaces()(AdminDashboard))
);



// WEBPACK FOOTER //
// ./src/containers/AdminDashboard/AdminDashboard.js